/**
 * Six digit boxes for the class code (docs/06-UI-SPEC.md §4).
 *
 * Typing moves to the next box on its own and pasting a whole code fills all six,
 * because a student reading a code aloud from a screen should never have to think
 * about which box they are in.
 */

import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

import { ROOM_CODE_LENGTH } from '@opal/shared';

export interface CodeEntryProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly invalid: boolean;
  /** True on touch devices, where the on-screen keypad types instead of the keyboard. */
  readonly useKeypad: boolean;
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, ROOM_CODE_LENGTH);
}

/** Keeps the caret on the box being filled, and handles typing, delete and paste. */
function useCodeBoxes(value: string, onChange: (value: string) => void, useKeypad: boolean) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!useKeypad) boxes.current[Math.min(value.length, ROOM_CODE_LENGTH - 1)]?.focus();
  }, [value, useKeypad]);

  return {
    boxes,
    typeAt: (index: number, typed: string): void => {
      const digit = digitsOnly(typed).slice(-1);
      if (digit === '') return;
      onChange(digitsOnly(`${value.slice(0, index)}${digit}${value.slice(index + 1)}`));
    },
    deleteAt: (event: KeyboardEvent<HTMLInputElement>, index: number): void => {
      if (event.key !== 'Backspace') return;
      event.preventDefault();
      const cut = Math.max(value.length > index ? index : value.length - 1, 0);
      onChange(`${value.slice(0, cut)}${value.slice(cut + 1)}`);
      boxes.current[cut]?.focus();
    },
    paste: (event: ClipboardEvent<HTMLInputElement>): void => {
      event.preventDefault();
      onChange(digitsOnly(event.clipboardData.getData('text')));
    },
  };
}

export function CodeEntry({ value, onChange, invalid, useKeypad }: CodeEntryProps) {
  const { boxes, typeAt, deleteAt, paste } = useCodeBoxes(value, onChange, useKeypad);

  return (
    <div className={`flex gap-2 ${invalid ? 'shake-once' : ''}`}>
      {Array.from({ length: ROOM_CODE_LENGTH }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            boxes.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={`Digit ${index + 1}`}
          readOnly={useKeypad}
          value={value[index] ?? ''}
          onChange={(event) => {
            typeAt(index, event.target.value);
          }}
          onKeyDown={(event) => {
            deleteAt(event, index);
          }}
          onPaste={paste}
          className={`h-16 w-14 rounded-md border bg-surface text-center text-2xl font-semibold text-text-primary ${
            invalid ? 'border-danger' : 'border-border-strong'
          }`}
        />
      ))}
    </div>
  );
}
